/*!
 * infinite-scroll.js — T4S (Taste) theme — collection + search pages
 * v9 (wireBtn variant) — todas as correções aplicadas
 *
 * SEO contract (unchanged, non-negotiable):
 *   ✓  <a rel="next"> pagination links stay in the DOM at all times (sr-only).
 *   ✓  Bots follow real paginated URLs — no JS-only navigation.
 *   ✓  Each ?page=N is a server-rendered page with unique content.
 *   ✓  Canonical = {{ canonical_url }} — not touched.
 *   ✓  JS disabled → standard pagination works, zero degradation.
 *
 * Correções aplicadas:
 *   v5 — _nextHref cursor explícito: lido uma vez do DOM, depois atualizado
 *         exclusivamente do HTML descarregado. Elimina o loop "sempre carrega
 *         a mesma página" causado por _nextURL() reler o DOM ao vivo.
 *   v6 — Visibilidade do botão via inline style (display none/block) em vez de
 *         depender de uma regra CSS .is-auto-mode que pode não existir no tema.
 *   v7 — Seletor de cards resiliente: tenta vários seletores em cascata até
 *         encontrar produtos na página descarregada.
 *   v8 — Finish imediato se appendedCount === 0: evita loop infinito quando o
 *         T4S renderiza um rel="next" espúrio em coleções pequenas.
 *   v9 — Esconde o progress bar nativo do T4S ([data-wrap-lm-bar]) no init,
 *         mantendo apenas o botão que é gerido pelo nosso script. O wrapper
 *         permanece no DOM (invisível) para crawlers SEO.
 */

(function () {
  'use strict';

  // ─── Configuration ───────────────────────────────────────────────────────────

  var TRIGGER_MARGIN = '0px 0px 900px 0px';
  var TRIGGER_PX     = 900;
  var MAX_AUTO_PAGES = 20;
  var SLOW_TIMEOUT   = 8000;

  /**
   * Seletores de cards tentados em cascata.
   * O primeiro que devolver >= 1 resultado vence e fica em cache.
   */
  var CARD_SELECTORS = [
    '.t4s-product',
    '.t4s-product-item',
    '[data-product-item]',
    '[data-product-id]',
    '.product-item',
    '.grid__item',
    'li',
    '> *'   // último recurso: todos os filhos diretos
  ];

  // ─── InfScroll class ────────────────────────────────────────────────────────

  function InfScroll(footerEl) {
    this.el          = footerEl;
    this.section     = footerEl.closest('[data-ntajax-container]') || footerEl.parentElement;
    this.grid        = this.section ? this.section.querySelector('[data-contentlm-replace]') : null;
    this.isLoading   = false;
    this.isDone      = false;
    this.autoMode    = false;
    this.pagesLoaded = 0;
    this._reqId      = 0;
    this.btn         = null;
    this.sentinel    = null;
    this.observer    = null;
    this.scroller    = null;
    this._onScroll   = null;
    this._slowTimer  = null;
    this._cardSel    = null;   // v7: seletor de cards em cache

    // v5: cursor explícito — lido uma vez aqui, depois atualizado do HTML descarregado.
    this._nextHref = this._extractNextHref(this.el);

    if (!this.grid) {
      console.warn('[infinite-scroll] grid não encontrado ([data-contentlm-replace])', this.section);
      return;
    }
    if (!this._nextHref) {
      // Catálogo de página única ou última página — nada a fazer.
      return;
    }

    this._setup();
  }

  // ─── URL cursor (v5) ─────────────────────────────────────────────────────────

  /**
   * Extrai o href da próxima página a partir de qualquer elemento raiz.
   * Tenta todos os padrões de paginação do T4S por ordem de prioridade.
   * Usado no init (DOM ao vivo) e após cada fetch (HTML descarregado).
   */
  InfScroll.prototype._extractNextHref = function (rootEl) {
    if (!rootEl) return null;
    var link =
      rootEl.querySelector('a.t4s-loadmore-btn[href*="page="]') ||
      rootEl.querySelector('a[rel="next"]') ||
      rootEl.querySelector('a[data-load-more][href*="page="]') ||
      rootEl.querySelector('.t4s-pagination-wrapper a[href*="page="]:not([rel="prev"])') ||
      rootEl.querySelector('a.t4s-next') ||
      rootEl.querySelector('.pagination a.next');
    return link ? link.href : null;
  };

  /**
   * Devolve o URL da próxima página.
   * v5: lê o campo _nextHref — nunca re-lê o DOM ao vivo.
   */
  InfScroll.prototype._nextURL = function () {
    return this._nextHref || null;
  };

  // ─── Card selector (v7) ──────────────────────────────────────────────────────

  /**
   * Encontra os cards de produto dentro de um grid element.
   * Tenta CARD_SELECTORS em cascata, em cache após a primeira resolução.
   */
  InfScroll.prototype._resolveCards = function (gridEl) {
    if (this._cardSel === '__children__') {
      return Array.prototype.slice.call(gridEl.children);
    }
    if (this._cardSel) {
      return Array.prototype.slice.call(gridEl.querySelectorAll(this._cardSel));
    }

    for (var i = 0; i < CARD_SELECTORS.length; i++) {
      var sel = CARD_SELECTORS[i];
      var found;
      try {
        found = (sel === '> *')
          ? Array.prototype.slice.call(gridEl.children)
          : Array.prototype.slice.call(gridEl.querySelectorAll(sel));
      } catch (e) { continue; }

      if (found.length > 0) {
        this._cardSel = (sel === '> *') ? '__children__' : sel;
        console.debug('[infinite-scroll] card selector:', this._cardSel, '→', found.length);
        return found;
      }
    }

    console.warn('[infinite-scroll] nenhum card encontrado no grid descarregado', gridEl);
    return [];
  };

  // ─── T4S neutralisation ──────────────────────────────────────────────────────

  /**
   * Remove os atributos que o T4S usa para event delegation.
   * O href e a class .t4s-loadmore-btn são preservados.
   */
  InfScroll.prototype._neutralizeT4S = function (root) {
    var btns = (root || this.el).querySelectorAll('[data-load-more], [data-load-onscroll]');
    Array.prototype.forEach.call(btns, function (btn) {
      btn.removeAttribute('data-load-more');
      btn.removeAttribute('data-load-onscroll');
    });
  };

  /**
   * v9: Esconde o progress bar nativo do T4S ("You've viewed X of Y products").
   * Mantém o botão visível — é gerido pelo nosso script via _showBtn/_hideBtn.
   * O wrapper permanece no DOM para crawlers SEO.
   */
  InfScroll.prototype._hideNativeProgressBar = function () {
    var bars = this.el.querySelectorAll('[data-wrap-lm-bar]');
    Array.prototype.forEach.call(bars, function (el) {
      el.style.display = 'none';
    });
  };

  /**
   * Clona o elemento do botão para remover listeners T4S existentes,
   * e regista o nosso click handler.
   */
  InfScroll.prototype._wireBtn = function (el) {
    if (!el) return null;
    var clone = el.cloneNode(true);
    el.parentNode.replaceChild(clone, el);
    var self = this;
    clone.addEventListener('click', function (e) {
      e.preventDefault();
      self._load();
    });
    return clone;
  };

  // ─── Setup ───────────────────────────────────────────────────────────────────

  InfScroll.prototype._setup = function () {
    this.el.classList.add('is-inf-active');

    // Capturar o botão ANTES de neutralizar (ainda tem data-load-more).
    var rawBtn = this.el.querySelector('a.t4s-loadmore-btn, a[data-load-more]');
    this.btn = this._wireBtn(rawBtn);

    if (!this.btn) {
      console.warn('[infinite-scroll] botão "Load more" não encontrado no DOM.');
    }

    // Neutralizar APÓS capturar o botão.
    this._neutralizeT4S();

    // v9: esconder progress bar nativo ("X of Y products").
    this._hideNativeProgressBar();

    // v6: esconder o botão por defeito — auto-mode trata de tudo.
    this._hideBtn();

    // Sentinel colocado imediatamente após o footer.
    this.sentinel = document.createElement('div');
    this.sentinel.className = 'is-inf-sentinel';
    this.sentinel.setAttribute('aria-hidden', 'true');
    this.el.insertAdjacentElement('afterend', this.sentinel);

    this._startAuto();
  };

  // ─── Button visibility (v6) ──────────────────────────────────────────────────

  /** Mostra o botão nativo do T4S — chamado quando auto-mode é pausado. */
  InfScroll.prototype._showBtn = function () {
    if (this.btn) this.btn.style.display = '';
  };

  /** Esconde o botão nativo do T4S — chamado ao entrar em auto-mode. */
  InfScroll.prototype._hideBtn = function () {
    if (this.btn) this.btn.style.display = 'none';
  };

  // ─── Scroll container detection ──────────────────────────────────────────────

  InfScroll.prototype._getScroller = function () {
    var el = this.section || this.el;
    while (el && el !== document.body) {
      try {
        var cs = window.getComputedStyle(el);
        if ((cs.overflowY === 'auto' || cs.overflowY === 'scroll') &&
            el.scrollHeight > el.clientHeight + 50) {
          return el;
        }
      } catch (e) {}
      el = el.parentElement;
    }
    return window;
  };

  InfScroll.prototype._sentinelNear = function () {
    if (!this.sentinel) return false;
    var rect = this.sentinel.getBoundingClientRect();
    var viewportBottom = (this.scroller === window)
      ? window.innerHeight
      : this.scroller.getBoundingClientRect().bottom;
    return rect.top <= viewportBottom + TRIGGER_PX;
  };

  // ─── Auto-mode ───────────────────────────────────────────────────────────────

  InfScroll.prototype._startAuto = function () {
    this.autoMode = true;
    this.el.classList.add('is-auto-mode');
    this._hideBtn(); // v6

    var self = this;
    this.scroller = this._getScroller();
    var scrollTarget = this.scroller;

    // 1. Verificação imediata.
    requestAnimationFrame(function () {
      if (!self.isLoading && !self.isDone && self._sentinelNear()) {
        self._load();
      }
    });

    // 2. Scroll event (rAF-throttled).
    var ticking = false;
    this._onScroll = function () {
      if (ticking || self.isLoading || self.isDone || !self.autoMode) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        if (!self.isLoading && !self.isDone && self._sentinelNear()) {
          self._load();
        }
      });
    };
    scrollTarget.addEventListener('scroll', this._onScroll, { passive: true });

    // 3. IntersectionObserver.
    if ('IntersectionObserver' in window) {
      var ioRoot = (this.scroller === window) ? null : this.scroller;
      this.observer = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting && !self.isLoading && !self.isDone) {
          self._load();
        }
      }, { root: ioRoot, rootMargin: TRIGGER_MARGIN });
      this.observer.observe(this.sentinel);
    }
  };

  InfScroll.prototype._pauseAuto = function () {
    this.autoMode = false;
    this.el.classList.remove('is-auto-mode');
    this._showBtn(); // v6: o utilizador precisa do botão para continuar

    if (this.observer) { this.observer.disconnect(); this.observer = null; }
    if (this._onScroll) {
      var scrollTarget = (this.scroller === window) ? window : this.scroller;
      scrollTarget.removeEventListener('scroll', this._onScroll);
      this._onScroll = null;
    }
    this.scroller = null;
  };

  // ─── Core load ───────────────────────────────────────────────────────────────

  InfScroll.prototype._load = function () {
    var url = this._nextURL();
    if (!url || this.isLoading || this.isDone) return;

    this.isLoading = true;
    var reqId = ++this._reqId;
    this._setBusy(true);

    var self = this;

    // Guarda de rede lenta.
    this._slowTimer = setTimeout(function () {
      self._slowTimer = null;
      if (self.isLoading) {
        console.warn('[infinite-scroll] Rede lenta — a mudar para "Load more" manual.');
        self._reqId++;
        self.isLoading = false;
        self._pauseAuto();
        self._setBusy(false);
      }
    }, SLOW_TIMEOUT);

    console.debug('[infinite-scroll] fetching:', url);

    fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        if (self._reqId !== reqId) return;
        clearTimeout(self._slowTimer);
        self._slowTimer = null;

        var doc = new DOMParser().parseFromString(html, 'text/html');

        // v5: avançar o cursor a partir do documento descarregado ANTES de
        //     qualquer manipulação DOM. Este é o único sítio onde _nextHref muda
        //     após o init.
        var fetchedFooter = doc.querySelector('.t4s-prs-footer[data-infinite-scroll]');
        self._nextHref = self._extractNextHref(fetchedFooter);
        console.debug('[infinite-scroll] next href:', self._nextHref);

        // ① Append dos novos cards de produto.
        var srcGrid = doc.querySelector('[data-contentlm-replace]');
        var appendedCount = 0;

        if (srcGrid) {
          // v7: seletor resiliente — resolve automaticamente.
          var cards = self._resolveCards(srcGrid);
          console.debug('[infinite-scroll] cards na página descarregada:', cards.length);
          cards.forEach(function (card) { self.grid.appendChild(card); });
          appendedCount = cards.length;

          if (appendedCount > 0) {
            document.dispatchEvent(new CustomEvent('lazyloadt4s:update'));
            if (window.lazySizes) {
              try { lazySizes.autoSizer.checkElms(); } catch (e) {}
            }
            if (window.T4SThemeSP && T4SThemeSP.reinitProductGridItem) {
              try { T4SThemeSP.reinitProductGridItem(); } catch (e) {}
            }
          }
        } else {
          console.warn('[infinite-scroll] [data-contentlm-replace] não encontrado em:', url);
        }

        // ② Trocar paginação (cosmético — navegação já gerida por _nextHref).
        //    Re-wire do botão novo + neutralização + esconder progress bar.
        if (fetchedFooter) {
          var oldNav = self.el.querySelector('.t4s-pagination-wrapper');
          var newNav = fetchedFooter.querySelector('.t4s-pagination-wrapper');
          if (oldNav && newNav) {
            oldNav.replaceWith(newNav);
            var newRawBtn = self.el.querySelector('a.t4s-loadmore-btn, a[data-load-more]');
            self.btn = self._wireBtn(newRawBtn);
            self._neutralizeT4S();
            self._hideNativeProgressBar(); // v9: esconder progress bar do novo nav
            if (!self.autoMode) {
              self._showBtn(); // só mostrar se estamos em modo manual
            } else {
              self._hideBtn();
            }
          }
        }

        // ③ Atualizar barra de endereço.
        var pageParam = null;
        try {
          pageParam = new URL(url).searchParams.get('page');
          if (pageParam) {
            var updated = new URL(window.location.href);
            updated.searchParams.set('page', pageParam);
            history.replaceState(null, '', updated.toString());
          }
        } catch (e) {}

        // ④ Analytics hook.
        document.dispatchEvent(new CustomEvent('is:page:appended', {
          bubbles: true,
          detail: { page: pageParam, count: appendedCount, grid: self.grid }
        }));

        // ⑤ Cap de DOM.
        self.pagesLoaded++;
        if (self.autoMode && self.pagesLoaded >= MAX_AUTO_PAGES) {
          self._pauseAuto();
        }

        // ⑥ Fim do catálogo:
        //    — sem next href, OU
        //    — v8: página descarregada devolveu 0 cards novos (T4S com rel="next"
        //      espúrio em coleções onde todos os produtos já estavam na página 1).
        if (!self._nextHref || appendedCount === 0) {
          console.debug('[infinite-scroll] finish — nextHref:', self._nextHref, 'appended:', appendedCount);
          self._finish();
          return;
        }

        // ⑦ Chain-load: sentinel ainda na zona de trigger?
        requestAnimationFrame(function () {
          if (self.autoMode && !self.isLoading && !self.isDone && self._sentinelNear()) {
            self._load();
          }
        });
      })
      .catch(function (err) {
        if (self._reqId !== reqId) return;
        clearTimeout(self._slowTimer);
        self._slowTimer = null;
        console.warn('[infinite-scroll] Falha no carregamento:', err);
        self._pauseAuto();
        self._setError();
      })
      .then(function () {
        if (self._reqId === reqId) {
          self.isLoading = false;
          if (self.btn && !self.isDone && !self.btn.classList.contains('is-error')) {
            self._setBusy(false);
          }
        }
      });
  };

  // ─── UI helpers ──────────────────────────────────────────────────────────────

  InfScroll.prototype._setBusy = function (on) {
    if (!this.btn) return;
    this.btn.disabled = on;
    this.btn.classList.toggle('is-loading', on);
    // Spinner nativo do T4S.
    var spinner = this.btn.querySelector('.t4s-loading__spinner');
    if (spinner) spinner.classList.toggle('t4s-dn', !on);
  };

  InfScroll.prototype._setError = function () {
    if (!this.btn) return;
    this.btn.disabled = false;
    this.btn.classList.remove('is-loading');
    this.btn.classList.add('is-error');
    var lbl = this.btn.querySelector('.t4s-btn-atc_text') || this.btn;
    lbl.textContent = 'Erro — clica para tentar novamente';
    var spinner = this.btn.querySelector('.t4s-loading__spinner');
    if (spinner) spinner.classList.add('t4s-dn');
    this._showBtn(); // garantir que está visível em caso de erro
  };

  InfScroll.prototype._finish = function () {
    this.isDone = true;
    if (this._slowTimer) { clearTimeout(this._slowTimer); this._slowTimer = null; }
    if (this.observer)   { this.observer.disconnect();    this.observer   = null; }
    if (this._onScroll)  {
      var scrollTarget = (this.scroller === window) ? window : this.scroller;
      scrollTarget.removeEventListener('scroll', this._onScroll);
      this._onScroll = null;
    }
    this.scroller = null;
    if (this.sentinel) { this.sentinel.remove(); this.sentinel = null; }
    // Remover o botão nativo do T4S — todos os produtos carregados.
    if (this.btn) { this.btn.remove(); this.btn = null; }
    this.el.classList.remove('is-inf-active', 'is-auto-mode');

    document.dispatchEvent(new CustomEvent('is:complete', {
      bubbles: true,
      detail: { totalPages: this.pagesLoaded }
    }));
  };

  // ─── Bootstrap ──────────────────────────────────────────────────────────────

  function boot() {
    document.querySelectorAll('.t4s-prs-footer[data-infinite-scroll]').forEach(function (el) {
      if (el._infScroll) return;
      el._infScroll = new InfScroll(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);

}());